import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { CircleCheck, Clock, LinkIcon, LoaderCircle, type LucideIcon } from 'lucide-react';
import { resetPasswordSchema, type ResetPasswordValues } from '@sf/shared';
import { homePath, useAuth } from '../../app/authContext';
import { Button } from '../../components/ui/Button';
import { PasswordField } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { t } from '../../lib/copy';
import { messageForError } from '../../lib/errors';
import { checkResetCode, confirmEmail, resendVerification, setNewPassword } from './api';

type Mode = 'verifyEmail' | 'resetPassword';
type View =
  | { kind: 'checking' }
  | { kind: 'verified' }
  | { kind: 'reset'; email: string }
  | { kind: 'resetDone' }
  | { kind: 'expired'; mode: Mode }
  | { kind: 'invalid' }
  | { kind: 'failed'; message: string };

const linkButton =
  'inline-flex h-10 w-full items-center justify-center rounded-input bg-brand px-4 text-sm font-medium text-white transition-colors hover:bg-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-light';

/** Firebase codes that mean the link was already used or is out of date. */
const isExpired = (err: unknown) =>
  ['auth/expired-action-code', 'auth/invalid-action-code'].includes(
    (err as { code?: string }).code ?? '',
  );

/**
 * Branded handler for links in auth emails (D34): `/auth/action?mode=…&oobCode=…`.
 * Replaces Firebase's default `/__/auth/action` page via the console's custom action URL.
 */
export function AuthActionPage() {
  const [params] = useSearchParams();
  const rawMode = params.get('mode');
  const oobCode = params.get('oobCode') ?? '';
  const mode: Mode | null =
    oobCode && (rawMode === 'verifyEmail' || rawMode === 'resetPassword') ? rawMode : null;
  const [state, setView] = useState<View>({ kind: 'checking' });
  const view: View = mode ? state : { kind: 'invalid' };
  // Codes are single-use, so never run the check twice (StrictMode remounts effects).
  const started = useRef<string | null>(null);

  useEffect(() => {
    if (!mode) return;
    const key = `${mode}:${oobCode}`;
    if (started.current === key) return;
    started.current = key;
    const work =
      mode === 'verifyEmail'
        ? confirmEmail(oobCode).then((): View => ({ kind: 'verified' }))
        : checkResetCode(oobCode).then((email): View => ({ kind: 'reset', email }));
    work.then(setView, (err: unknown) =>
      setView(
        isExpired(err)
          ? { kind: 'expired', mode }
          : { kind: 'failed', message: messageForError(err) },
      ),
    );
  }, [mode, oobCode]);

  switch (view.kind) {
    case 'checking':
      return (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-3 text-sm text-slate"
        >
          <LoaderCircle className="size-5 animate-spin text-brand" aria-hidden />
          {t('auth.action.checking')}
        </div>
      );
    case 'verified':
      return <VerifiedView />;
    case 'reset':
      return (
        <ResetForm
          oobCode={oobCode}
          email={view.email}
          onDone={() => setView({ kind: 'resetDone' })}
          onExpired={() => setView({ kind: 'expired', mode: 'resetPassword' })}
        />
      );
    case 'resetDone':
      return (
        <Result icon={CircleCheck} tone="green" title={t('auth.action.reset.done.title')}>
          <p>{t('auth.action.reset.done.body')}</p>
          <Link to="/login" className={linkButton}>
            Sign in
          </Link>
        </Result>
      );
    case 'expired':
      return view.mode === 'verifyEmail' ? (
        <VerifyExpiredView />
      ) : (
        <Result icon={Clock} tone="amber" title={t('auth.action.expired.title')}>
          <p>{t('auth.action.expired.reset')}</p>
          <Link to="/forgot-password" className={linkButton}>
            Request a new link
          </Link>
          <SignInLink />
        </Result>
      );
    case 'invalid':
      return (
        <Result icon={LinkIcon} tone="amber" title={t('auth.action.invalid.title')}>
          <p>{t('auth.action.invalid.body')}</p>
          <Link to="/login" className={linkButton}>
            Sign in
          </Link>
        </Result>
      );
    case 'failed':
      return (
        <Result icon={LinkIcon} tone="amber" title={t('auth.action.invalid.title')}>
          <p>{view.message}</p>
          <Button className="w-full justify-center" onClick={() => window.location.reload()}>
            Try again
          </Button>
          <SignInLink />
        </Result>
      );
  }
}

function VerifiedView() {
  const { user, profile } = useAuth();
  return (
    <Result icon={CircleCheck} tone="green" title={t('auth.action.verified.title')}>
      <p>{t('auth.action.verified.body')}</p>
      {user ? (
        <Link to={homePath(profile)} className={linkButton}>
          Go to your account
        </Link>
      ) : (
        <Link to="/login" className={linkButton}>
          Sign in
        </Link>
      )}
    </Result>
  );
}

function VerifyExpiredView() {
  const { user } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await resendVerification();
      setSent(true);
    } catch (err) {
      toast({ title: messageForError(err), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Result icon={Clock} tone="amber" title={t('auth.action.expired.title')}>
      {user ? (
        <>
          <p>{t('auth.action.expired.verifySignedIn')}</p>
          {sent ? (
            <p role="status" className="font-medium text-valley-700">
              {t('auth.verify.resent')}
            </p>
          ) : (
            <Button loading={busy} className="w-full justify-center" onClick={() => void resend()}>
              Send a new link
            </Button>
          )}
        </>
      ) : (
        <>
          <p>{t('auth.action.expired.verify')}</p>
          <Link to="/login" className={linkButton}>
            Sign in
          </Link>
        </>
      )}
    </Result>
  );
}

function ResetForm({
  oobCode,
  email,
  onDone,
  onExpired,
}: {
  oobCode: string;
  email: string;
  onDone: () => void;
  onExpired: () => void;
}) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordValues>({ resolver: zodResolver(resetPasswordSchema) });

  const onSubmit = handleSubmit(async ({ password }) => {
    try {
      await setNewPassword(oobCode, password);
      onDone();
    } catch (err) {
      if (isExpired(err)) return onExpired();
      if ((err as { code?: string }).code === 'auth/weak-password') {
        setError('password', { message: 'Use at least 8 characters.' });
        return;
      }
      toast({ title: messageForError(err), variant: 'error' });
    }
  });

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('auth.action.reset.title')}</h1>
      <p className="mt-1 text-sm break-all text-slate">{t('auth.action.reset.for', { email })}</p>
      <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
        {/* Lets password managers save the new password against the right account. */}
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <PasswordField
          label="New password"
          autoComplete="new-password"
          required
          hint="At least 8 characters."
          error={errors.password?.message}
          {...register('password')}
        />
        <PasswordField
          label="Confirm password"
          autoComplete="new-password"
          required
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" loading={isSubmitting} className="w-full justify-center">
          Save new password
        </Button>
      </form>
      <SignInLink />
    </div>
  );
}

function Result({
  icon: Icon,
  tone,
  title,
  children,
}: {
  icon: LucideIcon;
  tone: 'green' | 'amber';
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <span
        className={`flex size-12 items-center justify-center rounded-full ${
          tone === 'green' ? 'bg-valley-50 text-valley-700' : 'bg-sunrise-50 text-sunrise-700'
        }`}
      >
        <Icon className="size-6" aria-hidden />
      </span>
      <h1 className="mt-4 text-2xl font-bold">{title}</h1>
      <div className="mt-2 flex flex-col gap-6 text-sm text-slate">{children}</div>
    </div>
  );
}

function SignInLink() {
  return (
    <p className="mt-6 text-center text-sm">
      <Link to="/login" className="font-medium text-brand underline-offset-2 hover:underline">
        Back to sign in
      </Link>
    </p>
  );
}
